/**
 * Progress Component
 *
 * `intent` colours the indicator with a semantic status colour (a tenant theme
 * sets these too); `tone` with a colour tone (a hero's, a rule's state) and
 * wins over it. Both route through the indicator's own className — never an
 * arbitrary `[&>div]:bg-…` selector on Radix's internal DOM. The fill is the
 * share of `max` (100 unless said: "step 2 of 4" is `value={2} max={4}`).
 */

import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import type { Tone } from '@tonnie/design-tokens';
import { cn } from '@tonnie/foundation';

import { TONE_CLASSES } from '../lib/tone-classes';

import type { ProgressName } from './control-name';

export type ProgressIntent = 'default' | 'success' | 'info' | 'warning';

const INTENT_INDICATOR: Record<ProgressIntent, string> = {
  default: 'bg-primary',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
};

/** A bar shows no words of its own, so it must be named (see ProgressName). */
export type ProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  intent?: ProgressIntent;
  tone?: Tone;
} & ProgressName;

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, max = 100, intent = 'default', tone, ...props }, ref) => {
  const filled = Math.min(100, Math.max(0, ((value ?? 0) / max) * 100));
  return (
    <ProgressPrimitive.Root
      ref={ref}
      // Forwarded, not only read: Radix derives `aria-valuenow` and `data-state`
      // from it. Without it every bar announced itself as indeterminate.
      value={value}
      max={max}
      className={cn('relative h-2 w-full overflow-hidden rounded-pill bg-muted', className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-intent={intent}
        data-tone={tone}
        className={cn(
          'h-full w-full flex-1 transition-transform duration-500 motion-reduce:transition-none',
          tone ? TONE_CLASSES[tone].solid : INTENT_INDICATOR[intent]
        )}
        style={{ transform: `translateX(-${100 - filled}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});

Progress.displayName = ProgressPrimitive.Root.displayName;
